import { Typography } from '@mui/material';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UploadType } from 'further-types/files';
import { FileType } from 'further-ui/utils';
import FileDrop from 'further-ui/components/FileDrop';
import downloadXlxs from 'utils/downloadXlxs';
import useFileUpload, { UploadFileResult } from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import BankOperationDetails from '../ReconcileTransactions/BankOperation/BankOperationDetails';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { Column, Divider } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';

type Props = {
  uploadedFile?: UploadFileResult;
  onFileUpload: (file: UploadFileResult | null) => void;
  bankAccountId: string;
};

const UploadFile: React.FC<Props> = ({
  onFileUpload,
  uploadedFile,
  bankAccountId,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();

  const [bankAccounts] = api.bankAccount.listBankAccounts.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const bankAccount = bankAccounts.find(
    (account) => account.id === bankAccountId,
  );

  const [data] = api.bankStatement.getMostRecentOperation.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
    bankAccountId,
  });

  const downloadExampleFile = api.bankStatement.downloadExample.useMutation({
    onSuccess: (data) => {
      const currentDate = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      const filename = `Bank statement upload template - ${
        bankAccount?.accountName || 'Account'
      } - ${vehicle?.vehicleName} - ${currentDate}`;

      downloadXlxs(data.file, filename);
    },
  });

  const handleFileUpload = async (
    files: Array<{ fileData: File; fileName: string }>,
  ) => {
    if (!files?.[0]?.fileData) return;
    const uploadFileResult = await uploadFile(
      files[0].fileData,
      UploadType.LpBankStatements,
    );
    if (uploadFileResult.data) {
      onFileUpload(uploadFileResult.data);
    } else {
      error(uploadFileResult.error.message);
    }
  };

  const handleDownloadExample = () => {
    downloadExampleFile.mutate();
  };

  return (
    <Column spacing="lg" fullHeight>
      <Column spacing="xs">
        <Typography variant="h6">Upload bank statement</Typography>
        <Typography variant="body2">
          Please use our template statement file for copying your bank statement
          entries into.
          <br />
          It is best to download your bank statements in CSV so you can copy
          them into the template file format.
        </Typography>
        <Typography variant="body2">
          <TypographyButton
            text="Download our template"
            onClick={handleDownloadExample}
          />
        </Typography>
      </Column>
      <FileDrop
        inputTitle="Upload file"
        name="file"
        maxFiles={1}
        supportedFormats={[FileType.CSV, FileType.Excel, FileType.ExcelAlt]}
        onDropFile={handleFileUpload}
        files={
          uploadedFile
            ? [
                {
                  previewUri: uploadedFile.url,
                  fileName: uploadedFile.name ?? uploadedFile.url,
                },
              ]
            : []
        }
        onDeleteFile={() => {
          onFileUpload(null);
        }}
      />
      <Divider />
      <Column spacing="sm">
        <Typography variant="h6">Most recent transaction</Typography>
        {data && (
          <BankOperationDetails
            showActions={false}
            vehicleId={selectedVehicleId ?? ''}
            bankAccountId={data.bankAccountId}
            bankOperationId={data.id}
            date={data.date}
            description={data.description}
            counterparty={data.counterparty}
            spent={data.spent}
            received={data.received}
            accountCurrency={data.currency!}
          />
        )}
      </Column>
    </Column>
  );
};

export default UploadFile;
